import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Instagram } from "lucide-react";
export const Footer = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const {
    toast
  } = useToast();
  const location = useLocation();
  const isAboutPage = location.pathname === '/about';
  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      console.log("Attempting to subscribe:", {
        name,
        email
      });
      const {
        error
      } = await supabase.from('subscribers').insert([{
        email,
        name: name || email.split('@')[0] // Use provided name or fallback to email prefix
      }]);
      if (error) throw error;
      console.log("Successfully subscribed");
      toast({
        title: "You're subscribed to Slay",
        duration: 1000
      });

      // Clear the inputs and close dialog
      setEmail("");
      setName("");
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Error subscribing to newsletter:", error);
      toast({
        title: "Subscription failed",
        description: "Please try again later",
        variant: "destructive"
      });
    }
  };
  const handleLinkClick = () => {
    // Scroll to top when navigating to new pages
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 100);
  };
  const handleAboutClick = () => {
    // Scroll to top for About page navigation
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 100);
  };
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 10
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  return <footer className="relative w-full bg-gradient-to-br from-purple-900/90 via-black/95 to-pink-900/90 backdrop-blur-lg border-t border-white/10 mt-20 overflow-hidden">
      {/* Animated background gradient */}
      <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-blue-500/10 animate-gradient-x" />

      <div className="container relative mx-auto px-4 py-12 z-10">
        <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }}>
          {/* About Section */}
          <motion.div className="space-y-4 backdrop-blur-sm bg-black/20 p-6 rounded-xl border border-white/10" variants={itemVariants}>
            <h3 className="text-xl font-bold">
              <Link to="/about" onClick={handleAboutClick} className="text-transparent bg-clip-text bg-gradient-to-r from-[#9b87f5] to-[#D946EF] hover:opacity-80 transition-opacity">
                About Slay
              </Link>
            </h3>
            <p className="text-gray-300 leading-relaxed">We're a purpose-led LGBTQIA+ event ticketing platform. Born in Australia, inspired by our community's creativity in Manila, and built for the world. More than tickets, Slay will be where our community connects, creates, celebrates, and Slay together. </p>
            <div className="mt-4 space-y-1">
              <p className="text-gray-300">📤 support@slay.events</p>
              <p className="text-gray-300">📍 L7, 222 Exhibition Street, Melbourne Victoria 3000, Australia</p>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div className="space-y-4 backdrop-blur-sm bg-black/20 p-6 rounded-xl border border-white/10" variants={itemVariants}>
            <h3 className="text-xl font-bold bg-gradient-to-r from-[#7E69AB] to-[#9b87f5] bg-clip-text text-transparent">
              Quick Links
            </h3>
            <ul className="space-y-2">
              <motion.li whileHover={{
              x: 5
            }} transition={{
              type: "spring",
              stiffness: 300
            }}>
                <Link to="/terms-of-service" onClick={handleLinkClick} className="text-gray-300 hover:text-white transition-colors relative group">
                  <span className="relative">
                    Terms of Service
                    <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-[#9b87f5] to-[#D946EF] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                  </span>
                </Link>
              </motion.li>
              <motion.li whileHover={{
              x: 5
            }} transition={{
              type: "spring",
              stiffness: 300
            }}>
                <Link to="/privacy-policy" onClick={handleLinkClick} className="text-gray-300 hover:text-white transition-colors relative group">
                  <span className="relative">
                    Privacy Policy
                    <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-[#9b87f5] to-[#D946EF] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                  </span>
                </Link>
              </motion.li>
              <motion.li whileHover={{
              x: 5
            }} transition={{
              type: "spring",
              stiffness: 300
            }}>
                <Link to="/about" onClick={handleAboutClick} className="text-gray-300 hover:text-white transition-colors relative group">
                  <span className="relative">
                    About Slay
                    <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-[#9b87f5] to-[#D946EF] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                  </span>
                </Link>
              </motion.li>
              <motion.li whileHover={{
              x: 5
            }} transition={{
              type: "spring",
              stiffness: 300
            }}>
                <Link to="/contact" onClick={handleLinkClick} className="text-gray-300 hover:text-white transition-colors relative group">
                  <span className="relative">
                    Contact Us
                    <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-[#9b87f5] to-[#D946EF] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                  </span>
                </Link>
              </motion.li>
              <motion.li whileHover={{
              x: 5
            }} transition={{
              type: "spring",
              stiffness: 300
            }}>
                <Link to="/producers/login" onClick={handleLinkClick} className="text-gray-300 hover:text-white transition-colors relative group">
                  <span className="relative">
                    Producer Login
                    <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-[#9b87f5] to-[#D946EF] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                  </span>
                </Link>
              </motion.li>
              <motion.li whileHover={{
              x: 5
            }} transition={{
              type: "spring",
              stiffness: 300
            }}>
                <a href="https://www.instagram.com/slay.events.app" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors relative group flex items-center gap-2">
                  <span className="relative">
                    Our Instagram
                    <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-[#9b87f5] to-[#D946EF] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                  </span>
                  <Instagram className="h-4 w-4" />
                </a>
              </motion.li>
            </ul>
          </motion.div>

          {/* Newsletter Section */}
          <motion.div className="space-y-4 backdrop-blur-sm bg-black/20 p-6 rounded-xl border border-white/10" variants={itemVariants}>
            <h3 className="text-xl font-bold bg-gradient-to-r from-[#D946EF] to-[#9b87f5] bg-clip-text text-transparent">
              Newsletter
            </h3>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <div className="relative group">
                  <Input type="email" placeholder="Enter your email" className="bg-black/50 border-white/10 text-white placeholder:text-gray-400 w-full transition-all duration-300 focus:ring-2 focus:ring-[#9b87f5] group-hover:border-[#D946EF] cursor-pointer" value={email} onChange={e => setEmail(e.target.value)} onClick={() => setIsDialogOpen(true)} // Open dialog when input is clicked
                />
                  <div className="absolute inset-0 rounded-md bg-gradient-to-r from-[#9b87f5]/20 to-[#D946EF]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                </div>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md bg-[#221F26] text-white border-[#403E43]">
                <DialogHeader>
                  <DialogTitle className="text-center text-xl font-semibold text-white">
                    💌 Stay in the Loop!
                  </DialogTitle>
                  <p className="text-center text-gray-300 mt-2">
                    Get exclusive LGBTQIA+ event updates, special offers, and community news—straight to your inbox! 🌈✨
                  </p>
                </DialogHeader>
                <form onSubmit={handleSubscribe} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-white">Name</Label>
                    <Input id="name" value={name} onChange={e => setName(e.target.value)} placeholder="Enter your name" className="bg-[#403E43] border-[#6E59A5] text-white placeholder:text-gray-400" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-white">Email</Label>
                    <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter your email" required className="bg-[#403E43] border-[#6E59A5] text-white placeholder:text-gray-400" />
                  </div>
                  <Button type="submit" className="w-full bg-[#6E59A5] hover:bg-[#7E69AB] text-white transition-colors">
                    Submit
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            <Button onClick={() => setIsDialogOpen(true)} className="w-full bg-gradient-to-r from-[#9b87f5] to-[#D946EF] text-white hover:opacity-90 transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg hover:shadow-purple-500/20">
              Subscribe
            </Button>
            <p className="text-sm text-gray-400">
              Join our newsletter to stay updated with the latest events and community news.
            </p>
          </motion.div>
        </motion.div>
      </div>

      {/* Acknowledgment of Country */}
      <div className="border-t border-white/10 bg-black/30">
        <div className="container mx-auto px-4 py-5 text-center">
          <p className="text-xs md:text-sm text-gray-400 max-w-4xl mx-auto leading-relaxed">
            Slay acknowledges Aboriginal and Torres Strait Islander peoples as the Traditional Owners and custodians of the lands on which we celebrate, gather, and connect. We pay our respects to their Elders past, present, and emerging, honouring their rich culture and ongoing contributions to our communities.
          </p>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-white/10 bg-black/40">
        <div className="container mx-auto px-4 py-6 text-center text-gray-400 text-sm">
          © 2025 Slayverse Pty Ltd ABN 85 629 390 414. All rights reserved.
        </div>
      </div>
    </footer>;
};